<template>
  <AdminLayout>
    <template #content>
      <div class="product-page-heading">
        <div>
          <h1 class="h4 mb-1">Détail produit</h1>
          <p v-if="product" class="text-muted small mb-0">
            {{ product.sku || 'Sans code' }} · {{ product.rayon || 'Rayon non défini' }}
          </p>
        </div>
        <div class="d-flex flex-wrap justify-content-end gap-2">
          <button class="btn btn-outline-secondary btn-sm btn-icon-label" @click="goBack" title="Retour à la liste">
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>
          <button v-if="canUpdateProduct" class="btn btn-primary btn-sm btn-icon-label" @click="openEditDialog" title="Modifier">
            <i class="fa fa-edit"></i>
            <span class="btn-label ms-1">Modifier</span>
          </button>
          <button v-if="canAdjustProductStock && product?.product_type !== 'electronic'" class="btn btn-success btn-sm btn-icon-label" :disabled="!product" @click="openStockAdjustmentDialog" title="Ajuster le stock">
            <i class="fa fa-right-left"></i>
            <span class="btn-label ms-1">Ajuster stock</span>
          </button>
          <button class="btn btn-outline-primary btn-sm btn-icon-label" :disabled="!product || !product.barcode" @click="openProductLabelDialog" title="Imprimer étiquette">
            <i class="fa fa-barcode"></i>
            <span class="btn-label ms-1">Imprimer étiquette</span>
          </button>
          <AppLoadingButton v-if="canDeleteProduct" class="btn btn-danger btn-sm btn-icon-label" :loading="isDeleting" loading-label="Suppression..." @click="confirmDelete" title="Supprimer">
            <i class="fa fa-trash-alt"></i>
            <span class="btn-label ms-1">Supprimer</span>
          </AppLoadingButton>
        </div>
      </div>

      <div v-if="isLoading" class="alert alert-info">
        Chargement du produit...
      </div>

      <div v-else-if="!product" class="alert alert-danger">
        Produit introuvable.
      </div>

      <div v-else>
        <!-- Messages -->
        <div v-if="successMessage" class="alert alert-success py-2">
          {{ successMessage }}
        </div>
        <div v-if="errorMessage" class="alert alert-danger py-2">
          {{ errorMessage }}
        </div>
        <div v-if="missingPurchasePriceWarning" class="alert alert-warning py-2">
          Ajoutez le prix d’achat pour que le système calcule automatiquement le bénéfice.
        </div>
        <div v-if="hasNoProfitWarning" class="alert alert-danger py-2">
          Ce produit ne génère aucun bénéfice au prix de vente actuel.
        </div>
        <div class="card border-0 shadow-sm mb-3 product-summary-card">
          <div class="card-body product-summary-card-body">
            <div class="product-summary-layout">
              <div class="product-identity">
                <img :src="productImage(product)" alt="photo produit" class="product-summary-photo" />

                <div class="product-identity-body">
                  <div class="product-title-row">
                    <h2 class="product-title mb-0">{{ product.name }}</h2>
                    <div class="product-status-group">
                      <span class="badge" :class="product.is_active ? 'bg-success' : 'bg-secondary'">
                        {{ product.is_active ? 'Actif' : 'Inactif' }}
                      </span>
                      <span v-if="product.has_active_promotion" class="badge bg-success">
                        Promotion active
                      </span>
                      <span v-if="product.formula_enabled" class="badge bg-info text-dark">
                        Formule par lot
                      </span>
                      <span v-if="product.product_type === 'electronic'" class="badge bg-primary">
                        Produit électronique
                      </span>
                    </div>
                  </div>

                  <div class="product-meta-grid">
                    <div class="product-meta-item" :title="product.sku || 'Sans code'">
                      <span>Code</span>
                      <strong>{{ product.sku || '—' }}</strong>
                    </div>
                    <div class="product-meta-item" :title="product.barcode || 'Sans code-barres'">
                      <span>Code-barres</span>
                      <strong>{{ product.barcode || '—' }}</strong>
                    </div>
                    <div class="product-meta-item" :title="product.rayon || 'Sans rayon'">
                      <span>Rayon</span>
                      <strong>
                        <span class="rayon-badge" :style="{ borderColor: rayonColor(product.rayon_color) }">
                          <span class="rayon-color-dot" :style="{ backgroundColor: rayonColor(product.rayon_color) }"></span>
                          <span>{{ product.rayon || '—' }}</span>
                        </span>
                      </strong>
                    </div>
                    <div class="product-meta-item" :title="product.categorie || 'Sans catégorie'">
                      <span>Catégorie</span>
                      <strong>{{ product.categorie || '—' }}</strong>
                    </div>
                  </div>

                  <div class="product-description product-description-inline">
                    <span>Description</span>
                    <p>{{ product.description || 'Aucune description renseignée.' }}</p>
                  </div>
                </div>
              </div>

              <div class="product-summary-side">
                <div v-if="product.product_type !== 'electronic'" class="product-stock-highlight" :class="{ 'is-low': product.stock_quantity <= product.min_stock_quantity }">
                  <span class="product-stock-highlight-label">Stock actuel</span>
                  <strong>{{ product.stock_quantity }}</strong>
                  <div class="product-stock-highlight-meta">
                    <span>Min. {{ product.min_stock_quantity }}</span>
                    <span>Cumul {{ product.cumulative_stock_quantity ?? 0 }}</span>
                  </div>
                  <span
                    class="badge mt-2 align-self-start"
                    :class="product.allow_negative_stock_sales ? 'bg-warning text-dark' : 'bg-secondary'"
                  >
                    Stock négatif {{ product.allow_negative_stock_sales ? 'autorisé' : 'bloqué' }}
                  </span>
                </div>
                <div v-else class="product-stock-highlight">
                  <span class="product-stock-highlight-label">Portefeuille électronique</span>
                  <strong class="fs-6">{{ product.electronic_wallet?.name || 'Non renseigné' }}</strong>
                  <small>{{ ({ recharge: 'Recharge', deposit: 'Dépôt', withdrawal: 'Retrait' })[product.electronic_operation_type] || '—' }}</small>
                </div>

                <div class="product-kpi-grid">
                  <div class="product-kpi">
                    <span>Prix d'achat</span>
                    <strong class="product-kpi-value">{{ $format.currency(product.purchase_price) }}</strong>
                  </div>
                  <div class="product-kpi">
                    <span>Prix de vente</span>
                    <strong v-if="product.has_active_promotion" class="product-kpi-value text-success">
                      {{ $format.currency(product.effective_sale_price || 0) }}
                    </strong>
                    <strong v-else class="product-kpi-value">{{ $format.currency(product.sale_price) }}</strong>
                    <small v-if="product.has_active_promotion" class="text-muted text-decoration-line-through">
                      {{ $format.currency(product.sale_price || 0) }}
                    </small>
                  </div>
                  <div class="product-kpi">
                    <span>Coefficient PV/PA</span>
                    <strong class="product-kpi-value" :class="coefficientClass(product.sale_price_coefficient)">
                      {{ coefficientLabel(product.sale_price_coefficient) }}
                    </strong>
                    <small>Bénéfice unitaire</small>
                    <strong class="product-kpi-value" :class="unitProfitClass">
                      {{ unitProfit === null ? '—' : $format.currency(unitProfit) }}
                    </strong>
                  </div>
                  <div class="product-kpi">
                    <span>Fournisseur défaut</span>
                    <strong class="product-kpi-value product-kpi-truncate" :title="defaultSupplierName">
                      {{ defaultSupplierName }}
                    </strong>
                    <small v-if="defaultSupplierPurchasePrice !== null">
                      {{ $format.currency(defaultSupplierPurchasePrice) }}
                      <span v-if="isDefaultSupplierSystem"> · Système</span>
                    </small>
                  </div>
                  <div class="product-kpi product-kpi-wide">
                    <span>Fiscalité</span>
                    <div class="d-flex flex-wrap gap-2">
                      <span class="badge" :class="product.is_tva_applicable ? 'bg-success' : 'bg-secondary'">
                        TVA {{ product.is_tva_applicable ? 'active' : 'inactive' }}
                      </span>
                      <span class="badge" :class="product.is_css_applicable ? 'bg-success' : 'bg-secondary'">
                        CSS {{ product.is_css_applicable ? 'active' : 'inactive' }}
                      </span>
                    </div>
                  </div>
                  <div v-if="product.active_promotion" class="product-kpi product-kpi-wide">
                    <span>Promotion</span>
                    <strong>Jusqu’au {{ $format.datetime(product.active_promotion.ends_at) }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card border-0 shadow-sm product-detail-tabs-card">
          <div class="card-body">
            <PrimevueTabView>
              <PrimevueTabPanel header="Photos">
                <ProductPhotosGallery :productId="product.id" @changed="onPhotosChanged" />
              </PrimevueTabPanel>

              <PrimevueTabPanel :header="`Fournisseurs (${product.supplier_offers?.length || 0})`">
                <div v-if="canUpdateProduct" class="d-flex justify-content-end mb-3">
                  <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="openAttachSupplierDialog">
                    <i class="fa fa-plus"></i>
                    <span class="btn-label ms-1">Ajouter</span>
                  </button>
                </div>

                <BootstrapDataTable
                  :rows="supplierOfferRows"
                  :columns="supplierColumns"
                  :per-page="10"
                  searchable
                  empty-text="Aucun fournisseur attaché à ce produit."
                  print-title="Fournisseurs du produit"
                  export-file-name="fournisseurs-produit.xlsx"
                  export-sheet-name="Fournisseurs"
                >
                  <template #cell-supplier_name="{ row }">
                    <div class="supplier-cell">
                      <div class="fw-semibold text-truncate">{{ row.supplier_name }}</div>
                      <div class="supplier-badges">
                        <span v-if="row.is_default" class="badge bg-warning text-dark">Défaut</span>
                        <span v-if="isSystemSupplierOffer(row)" class="badge bg-light text-dark border">Système</span>
                      </div>
                    </div>
                  </template>
                  <template #cell-purchase_price="{ row }">
                    <span class="fw-semibold">{{ $format.currency(row.purchase_price || 0) }}</span>
                  </template>
                  <template #cell-lead_time_days="{ row }">
                    {{ row.lead_time_days !== null ? `${row.lead_time_days} j` : '—' }}
                  </template>
                  <template #cell-min_order_quantity="{ row }">
                    {{ row.min_order_quantity || '—' }}
                  </template>
                  <template #cell-supplier_sku="{ row }">
                    {{ row.supplier_sku || '—' }}
                  </template>
                  <template #cell-status="{ row }">
                    <span class="badge" :class="row.is_active ? 'bg-success' : 'bg-secondary'">
                      {{ row.is_active ? 'Actif' : 'Inactif' }}
                    </span>
                  </template>
                </BootstrapDataTable>
              </PrimevueTabPanel>

              <PrimevueTabPanel :header="`Historique ventes validées (${salesHistory.length})`">
                <BootstrapDataTable
                  :rows="salesHistoryRows"
                  :columns="salesHistoryColumns"
                  :loading="isLoadingSalesHistory"
                  :per-page="10"
                  searchable
                  empty-text="Aucune vente validée pour ce produit."
                  print-title="Historique des ventes validées du produit"
                  export-file-name="ventes-validees-produit.xlsx"
                  export-sheet-name="Ventes validées"
                >
                  <template #cell-reference="{ row }">
                    <router-link :to="{ name: 'SaleShow', params: { id: row.sale_id } }" class="fw-semibold">
                      {{ row.reference || `#${row.sale_id}` }}
                    </router-link>
                    <div v-if="row.payment_method" class="text-muted small">{{ paymentMethodLabel(row.payment_method) }}</div>
                  </template>
                  <template #cell-validated_at="{ row }">{{ row.validated_at ? $format.datetime(row.validated_at) : '—' }}</template>
                  <template #cell-net_quantity="{ row }">
                    {{ row.net_quantity }}
                    <span v-if="row.cancelled_quantity" class="text-muted small">/ {{ row.quantity }}</span>
                  </template>
                  <template #cell-unit_price="{ row }">
                    <div v-if="isPromotionSaleLine(row)" class="text-muted text-decoration-line-through small">
                      {{ $format.currency(row.base_unit_price || row.unit_price || 0) }}
                    </div>
                    <div class="fw-semibold">{{ $format.currency(row.unit_price || 0) }}</div>
                    <span v-if="isPromotionSaleLine(row)" class="badge bg-success">
                      {{ row.price_source === 'cashier_promotion' ? 'Promo caisse' : 'Promo' }}
                    </span>
                  </template>
                  <template #cell-sale_price_coefficient="{ row }">
                    <span v-if="row.sale_price_coefficient !== null && row.sale_price_coefficient !== undefined" class="badge" :class="coefficientBadgeClass(row.sale_price_coefficient)">
                      {{ coefficientLabel(row.sale_price_coefficient) }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </template>
                  <template #cell-total_line="{ row }"><span class="fw-semibold">{{ $format.currency(row.total_line || 0) }}</span></template>
                </BootstrapDataTable>
              </PrimevueTabPanel>

              <PrimevueTabPanel :header="`Historique achats validés (${purchasesHistory.length})`">
                <BootstrapDataTable
                  :rows="purchasesHistoryRows"
                  :columns="purchasesHistoryColumns"
                  :loading="isLoadingPurchasesHistory"
                  :per-page="10"
                  searchable
                  empty-text="Aucun achat validé pour ce produit."
                  print-title="Historique des achats validés du produit"
                  export-file-name="achats-valides-produit.xlsx"
                  export-sheet-name="Achats validés"
                >
                  <template #cell-reference="{ row }">
                    <router-link :to="{ name: 'PurchaseShow', params: { id: row.purchase_id } }" class="fw-semibold">
                      {{ row.reference || `#${row.purchase_id}` }}
                    </router-link>
                    <div v-if="row.validated_at" class="text-muted small">Validé le {{ $format.datetime(row.validated_at) }}</div>
                  </template>
                  <template #cell-purchase_date="{ row }">{{ row.purchase_date ? $format.date(row.purchase_date) : '—' }}</template>
                  <template #cell-unit_price="{ row }">{{ $format.currency(row.unit_price || 0) }}</template>
                  <template #cell-line_total="{ row }"><span class="fw-semibold">{{ $format.currency(row.line_total || 0) }}</span></template>
                  <template #cell-lot_number="{ row }">{{ row.lot_number || '—' }}</template>
                </BootstrapDataTable>
              </PrimevueTabPanel>

              <PrimevueTabPanel :header="`Mouvements (${movementRowsTotal})`">
                <div class="card module-filter-card mb-3">
                  <div class="card-body py-2">
                    <div class="row g-2 align-items-end">
                      <div class="col-lg-3 col-md-6">
                        <label class="form-label small mb-1">Du</label>
                        <input v-model="movementFilters.date_from" type="date" class="form-control form-control-sm" />
                      </div>

                      <div class="col-lg-3 col-md-6">
                        <label class="form-label small mb-1">Au</label>
                        <input v-model="movementFilters.date_to" type="date" class="form-control form-control-sm" />
                      </div>

                      <div class="col-lg-3 col-md-6">
                        <label class="form-label small mb-1">Impact</label>
                        <PrimevueDropdown
                          v-model="movementFilters.impact"
                          :options="movementImpactOptions"
                          optionLabel="label"
                          optionValue="value"
                          class="w-100"
                        />
                      </div>

                      <div class="col-lg-3 col-md-6 module-filter-actions mt-2">
                        <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetMovementFilters">
                          <i class="fa fa-rotate-left"></i>
                          <span class="btn-label ms-1">Réinitialiser</span>
                        </button>
                        <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="applyMovementFilters">
                          <i class="fa fa-search"></i>
                          <span class="btn-label ms-1">Filtrer</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <BootstrapDataTable
                  :response="movementsResponse"
                  :columns="movementColumns"
                  :loading="isLoadingMovements"
                  :sort-by="movementTable.sortBy"
                  :sort-dir="movementTable.sortDir"
                  :per-page="movementTable.perPage"
                  empty-text="Aucun mouvement pour ce produit sur la période."
                  print-title="Mouvements du produit"
                  export-file-name="mouvements-produit.xlsx"
                  export-sheet-name="Mouvements"
                  @sort-change="handleMovementSortChange"
                  @page-change="handleMovementPageChange"
                  @per-page-change="handleMovementPerPageChange"
                  @refresh="fetchProductMovements"
                >
                  <template #cell-occurred_at="{ row }">
                    {{ row.occurred_at ? $format.datetime(row.occurred_at) : '—' }}
                  </template>

                  <template #cell-impact="{ row }">
                    <span class="badge" :class="movementImpactClass(row)">
                      {{ movementImpactLabel(row) }}
                    </span>
                  </template>

                  <template #cell-signed_quantity="{ row }">
                    <span :class="Number(row.signed_quantity || 0) >= 0 ? 'text-success' : 'text-danger'">
                      {{ Number(row.signed_quantity || 0) >= 0 ? '+' : '' }}{{ row.signed_quantity }}
                    </span>
                  </template>

                  <template #cell-stock="{ row }">
                    {{ row.stock_before ?? '—' }} → {{ row.stock_after ?? '—' }}
                  </template>

                  <template #cell-reason="{ row }">
                    <div v-if="row.reason">
                      <div class="fw-semibold">{{ row.reason.label }}</div>
                      <small class="text-muted">{{ reasonTypeLabel(row.reason.type) }}</small>
                    </div>
                    <span v-else class="text-muted">—</span>
                  </template>

                  <template #cell-source_type="{ row }">
                    {{ sourceLabel(row.source_type) }}
                  </template>

                  <template #cell-lot_number="{ row }">
                    {{ row.lot_number || '—' }}
                  </template>

                  <template #cell-expiry_date="{ row }">
                    {{ row.expiry_date ? $format.date(row.expiry_date) : '—' }}
                  </template>

                  <template #cell-user="{ row }">
                    {{ row.user?.name || '—' }}
                  </template>
                </BootstrapDataTable>
              </PrimevueTabPanel>
            </PrimevueTabView>
          </div>
        </div>

        <!-- Dialog édition -->
        <PrimevueDialog v-model:visible="dialogVisible" :modal="true" :closable="false" :style="{ width: '900px', maxWidth: '95vw' }">
          <template #header>
            <h5 class="modal-title mb-0">Modifier le produit</h5>
          </template>

          <ProductForm mode="edit" :product="product" @saved="handleSaved" @cancel="closeDialog" />
        </PrimevueDialog>

        <PrimevueDialog v-model:visible="stockAdjustmentDialogVisible" :modal="true" :closable="false" :style="{ width: '560px', maxWidth: '95vw' }">
          <template #header>
            <h5 class="modal-title mb-0">Ajuster le stock</h5>
          </template>

          <form @submit.prevent="submitStockAdjustment">
            <div class="alert alert-light border py-2 small mb-3">
              Stock actuel de <strong>{{ product.name }}</strong> :
              <span class="fw-semibold">{{ product.stock_quantity }}</span>
            </div>

            <div class="row g-3">
              <div class="col-12">
                <label class="form-label">Motif</label>
                <PrimevueDropdown
                  v-model="stockAdjustmentForm.stock_movement_reason_id"
                  :options="stockAdjustmentReasonOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Sélectionner un motif"
                  class="w-100"
                  :loading="isLoadingMovementReasons"
                  required
                />
                <small class="text-muted">Le motif est obligatoire et définit automatiquement l'entrée ou la sortie.</small>
                <div v-if="!isLoadingMovementReasons && !stockAdjustmentReasonOptions.length" class="alert alert-warning py-2 mt-2 mb-0 small">
                  Aucun motif manuel actif n'est disponible pour ce site. Ajoutez un motif standard dans Réglages mouvements.
                </div>
              </div>

              <div v-if="selectedAdjustmentReason" class="col-12">
                <div class="d-flex flex-wrap gap-2 align-items-center small">
                  <span class="badge" :class="selectedAdjustmentReason.impact === 'entry' ? 'bg-success' : 'bg-danger'">
                    {{ impactLabel(selectedAdjustmentReason.impact) }}
                  </span>
                  <span v-if="selectedAdjustmentReason.is_supplier_return" class="badge bg-warning text-dark">
                    Retour fournisseur
                  </span>
                  <span v-if="selectedAdjustmentReason.is_supplier_return && !hasDefaultSupplier" class="text-danger">
                    Aucun fournisseur par défaut actif n'est attaché à ce produit.
                  </span>
                </div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Quantité</label>
                <PrimevueInputNumber v-model="stockAdjustmentForm.quantity" class="w-100" :min="1" />
              </div>

              <div class="col-12">
                <label class="form-label">Commentaire</label>
                <PrimevueTextarea
                  v-model="stockAdjustmentForm.notes"
                  class="w-100"
                  rows="3"
                  autoResize
                  placeholder="Précision optionnelle"
                />
              </div>
            </div>

            <div v-if="stockAdjustmentError" class="alert alert-danger py-2 mt-3">
              {{ stockAdjustmentError }}
            </div>

            <div class="d-flex justify-content-end gap-2 mt-4">
              <button type="button" class="btn btn-outline-secondary btn-icon-label" :disabled="isSavingStockAdjustment" @click="closeStockAdjustmentDialog">
                <i class="fa fa-xmark"></i>
                <span class="btn-label ms-1">Annuler</span>
              </button>
              <AppLoadingButton
                type="submit"
                class="btn btn-success btn-icon-label"
                :loading="isSavingStockAdjustment"
                :disabled="!stockAdjustmentForm.stock_movement_reason_id"
                loading-label="Enregistrement..."
              >
                <i class="fa fa-check"></i>
                <span class="btn-label ms-1">Valider</span>
              </AppLoadingButton>
            </div>
          </form>
        </PrimevueDialog>

        <PrimevueDialog v-model:visible="supplierAttachDialogVisible" :modal="true" :closable="false" :style="{ width: '720px', maxWidth: '95vw' }">
          <template #header>
            <h5 class="modal-title mb-0">Ajouter un fournisseur</h5>
          </template>

          <form @submit.prevent="submitSupplierAttach">
            <div v-if="supplierAttachError" class="alert alert-danger py-2">
              {{ supplierAttachError }}
            </div>

            <div v-if="!availableSupplierOptions.length && !isLoadingSuppliers" class="alert alert-info py-2">
              Tous les fournisseurs actifs sont déjà attachés à ce produit.
            </div>

            <div class="row g-3">
              <div class="col-12">
                <label class="form-label">Fournisseur</label>
                <PrimevueDropdown
                  v-model="supplierAttachForm.supplier_id"
                  :options="availableSupplierOptions"
                  optionLabel="name"
                  optionValue="id"
                  placeholder="Sélectionner un fournisseur"
                  class="w-100"
                  :loading="isLoadingSuppliers"
                  filter
                  required
                />
              </div>

              <div class="col-md-6">
                <label class="form-label">Prix d'achat</label>
                <PrimevueInputNumber
                  v-model="supplierAttachForm.purchase_price"
                  class="w-100"
                  mode="decimal"
                  :min="0"
                  :maxFractionDigits="2"
                />
              </div>

              <div class="col-md-3">
                <label class="form-label">Délai (jours)</label>
                <PrimevueInputNumber v-model="supplierAttachForm.lead_time_days" class="w-100" :min="0" />
              </div>

              <div class="col-md-3">
                <label class="form-label">Qté min.</label>
                <PrimevueInputNumber v-model="supplierAttachForm.min_order_quantity" class="w-100" :min="1" />
              </div>

              <div class="col-md-6">
                <label class="form-label">Réf. fournisseur</label>
                <PrimevueInputText v-model.trim="supplierAttachForm.supplier_sku" class="w-100" placeholder="Optionnel" />
              </div>

              <div class="col-md-3 d-flex align-items-end">
                <div class="form-check">
                  <PrimevueCheckbox v-model="supplierAttachForm.is_default" binary inputId="attachSupplierDefault" />
                  <label for="attachSupplierDefault" class="form-check-label ms-2">Défaut</label>
                </div>
              </div>

              <div class="col-md-3 d-flex align-items-end">
                <div class="form-check">
                  <PrimevueCheckbox v-model="supplierAttachForm.is_active" binary inputId="attachSupplierActive" />
                  <label for="attachSupplierActive" class="form-check-label ms-2">Actif</label>
                </div>
              </div>

              <div class="col-12">
                <label class="form-label">Notes</label>
                <PrimevueTextarea
                  v-model="supplierAttachForm.notes"
                  class="w-100"
                  rows="3"
                  autoResize
                  placeholder="Conditions, remarques, contact spécifique..."
                />
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-4">
              <button type="button" class="btn btn-outline-secondary btn-icon-label" :disabled="isSavingSupplierAttach" @click="closeAttachSupplierDialog">
                <i class="fa fa-xmark"></i>
                <span class="btn-label ms-1">Annuler</span>
              </button>
              <button type="submit" class="btn btn-primary btn-icon-label" :disabled="isSavingSupplierAttach || !availableSupplierOptions.length">
                <i class="fa" :class="isSavingSupplierAttach ? 'fa-spinner fa-spin' : 'fa-check'"></i>
                <span class="btn-label ms-1">Attacher</span>
              </button>
            </div>
          </form>
        </PrimevueDialog>

        <ProductLabelPrintDialog
          v-model:visible="labelDialogVisible"
          :product-name="product.name"
          :sale-price="product.sale_price"
          :barcode="product.barcode"
        />
      </div>

    </template>
  </AdminLayout>
</template>

<script>
import ProductLabelPrintDialog from '@/components/ProductLabelPrintDialog.vue';
import ProductForm from './ProductForm.vue';
import ProductPhotosGallery from './ProductPhotosGallery.vue';
import { productImageUrl } from '@/support/productImages';
import { extractApiTotal } from '@/support/moduleFilters';
import { normalizeRayonColor } from '@/utils/rayonColor';
import { hasPermission } from '@/support/permissions';

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const defaultMovementFilters = () => {
  const dateTo = new Date();
  const dateFrom = new Date(dateTo);
  dateFrom.setDate(dateFrom.getDate() - 30);

  return {
    date_from: formatInputDate(dateFrom),
    date_to: formatInputDate(dateTo),
    impact: '',
  };
};

export default {
  name: 'ProductShowView',
  components: {
    ProductLabelPrintDialog,
    ProductForm,
    ProductPhotosGallery
  },
  data() {
    return {
      product: null,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      dialogVisible: false,
      labelDialogVisible: false,
      stockAdjustmentDialogVisible: false,
      supplierAttachDialogVisible: false,
      isSavingStockAdjustment: false,
      isSavingSupplierAttach: false,
      isLoadingMovementReasons: false,
      stockAdjustmentError: '',
      supplierAttachError: '',
      isDeleting: false,
      movementReasons: [],
      suppliers: [],
      isLoadingSuppliers: false,
      salesHistory: [],
      purchasesHistory: [],
      isLoadingSalesHistory: false,
      isLoadingPurchasesHistory: false,
      supplierColumns: [
        { key: 'supplier_name', label: 'Fournisseur', sortable: true },
        { key: 'purchase_price', label: "Prix d'achat", sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'lead_time_days', label: 'Délai', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'min_order_quantity', label: 'Qté min.', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'supplier_sku', label: 'Réf. fournisseur', sortable: true },
        { key: 'status', label: 'Statut', sortable: true },
      ],
      salesHistoryColumns: [
        { key: 'reference', label: 'Vente', sortable: true },
        { key: 'customer_name', label: 'Client', sortable: true },
        { key: 'validated_at', label: 'Date validation', sortable: true },
        { key: 'net_quantity', label: 'Qté', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'unit_price', label: 'PU', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'sale_price_coefficient', label: 'Coef.', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'total_line', label: 'Total', sortable: true, class: 'text-end', headerClass: 'text-end' },
      ],
      purchasesHistoryColumns: [
        { key: 'reference', label: 'Achat', sortable: true },
        { key: 'supplier_name', label: 'Fournisseur', sortable: true },
        { key: 'purchase_date', label: 'Date achat', sortable: true },
        { key: 'quantity', label: 'Qté', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'unit_price', label: 'PU', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'line_total', label: 'Total', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'lot_number', label: 'Lot', sortable: true },
      ],
      movementsResponse: null,
      isLoadingMovements: false,
      movementFilters: defaultMovementFilters(),
      movementTable: {
        sortBy: 'occurred_at',
        sortDir: 'desc',
        page: 1,
        perPage: 10,
      },
      movementColumns: [
        { key: 'occurred_at', label: 'Date', sortable: true },
        { key: 'impact', label: 'Impact', class: 'text-center' },
        { key: 'signed_quantity', label: 'Qté', sortable: true, class: 'text-center' },
        { key: 'stock', label: 'Stock', class: 'text-center' },
        { key: 'reason', label: 'Motif' },
        { key: 'source_type', label: 'Source', sortable: true },
        { key: 'lot_number', label: 'Lot' },
        { key: 'expiry_date', label: 'Expiration' },
        { key: 'user', label: 'Utilisateur' },
      ],
      movementImpactOptions: [
        { value: '', label: 'Tous' },
        { value: 'entry', label: 'Entrées' },
        { value: 'exit', label: 'Sorties' },
      ],
      stockAdjustmentForm: {
        stock_movement_reason_id: '',
        quantity: 1,
        notes: '',
      },
      supplierAttachForm: {
        supplier_id: null,
        purchase_price: 0,
        lead_time_days: null,
        min_order_quantity: null,
        supplier_sku: '',
        is_default: false,
        is_active: true,
        notes: '',
      },
    };
  },
  mounted() {
    this.fetchProduct();
    this.fetchMovementReasons();
  },
  computed: {
    supplierOfferRows() {
      return (this.product?.supplier_offers || []).map((offer) => ({
        ...offer,
        supplier_name: offer.supplier?.name || 'Fournisseur',
        status: offer.is_active ? 'Actif' : 'Inactif',
      }));
    },
    salesHistoryRows() {
      return this.salesHistory.map((line) => ({
        ...line,
        customer_name: line.customer_name || 'Client comptant',
        net_quantity: line.net_quantity ?? line.quantity,
      }));
    },
    purchasesHistoryRows() {
      return this.purchasesHistory.map((line) => ({
        ...line,
        supplier_name: line.supplier_name || 'Fournisseur',
      }));
    },
    activeStandardReasons() {
      return this.movementReasons.filter((reason) => reason.is_manual_selectable && reason.is_active);
    },
    stockAdjustmentReasonOptions() {
      return this.activeStandardReasons.map((reason) => ({
        label: `${reason.label} - ${this.impactLabel(reason.impact)}`,
        value: reason.id,
      }));
    },
    selectedAdjustmentReason() {
      return this.activeStandardReasons.find((reason) => String(reason.id) === String(this.stockAdjustmentForm.stock_movement_reason_id)) || null;
    },
    hasDefaultSupplier() {
      return Boolean(this.product?.supplier_offers?.some((offer) => offer.is_default && offer.is_active));
    },
    defaultSupplierOffer() {
      const offers = this.product?.supplier_offers || [];

      return offers.find((offer) => offer.is_default && offer.is_active)
        || offers.find((offer) => offer.is_default)
        || null;
    },
    defaultSupplierName() {
      return this.defaultSupplierOffer?.supplier?.name
        || this.product?.default_supplier?.name
        || '—';
    },
    defaultSupplierPurchasePrice() {
      if (this.defaultSupplierOffer) {
        return Number(this.defaultSupplierOffer.purchase_price || 0);
      }

      if (this.product?.default_supplier) {
        return Number(this.product.default_supplier.purchase_price || 0);
      }

      return null;
    },
    isDefaultSupplierSystem() {
      return this.isSystemSupplierOffer(this.defaultSupplierOffer)
        || this.product?.default_supplier?.type === 'system'
        || this.product?.default_supplier?.system_key === 'default';
    },
    attachedSupplierIds() {
      return new Set((this.product?.supplier_offers || [])
        .map((offer) => offer.supplier_id || offer.supplier?.id)
        .filter(Boolean));
    },
    availableSupplierOptions() {
      return this.suppliers.filter((supplier) => !this.attachedSupplierIds.has(supplier.id));
    },
    movementRowsTotal() {
      const fallback = Array.isArray(this.movementsResponse?.data)
        ? this.movementsResponse.data.length
        : 0;

      return extractApiTotal(this.movementsResponse, fallback);
    },
    hasNoProfitWarning() {
      if (!this.hasPurchasePriceForProfit || !this.hasSalePriceForProfit) {
        return false;
      }

      const coefficient = Number(this.product?.sale_price_coefficient);

      return Number.isFinite(coefficient) && coefficient <= 1;
    },
    missingPurchasePriceWarning() {
      return this.hasSalePriceForProfit && !this.hasPurchasePriceForProfit;
    },
    hasPurchasePriceForProfit() {
      const purchasePrice = Number(this.product?.purchase_price || 0);

      return Number.isFinite(purchasePrice) && purchasePrice > 0;
    },
    hasSalePriceForProfit() {
      const salePrice = Number(this.product?.effective_sale_price ?? this.product?.sale_price ?? 0);

      return Number.isFinite(salePrice) && salePrice > 0;
    },
    unitProfit() {
      if (!this.hasPurchasePriceForProfit || !this.hasSalePriceForProfit) return null;

      return Number(this.product.effective_sale_price ?? this.product.sale_price) - Number(this.product.purchase_price);
    },
    unitProfitClass() {
      if (this.unitProfit === null || this.unitProfit === 0) return 'text-muted';
      return this.unitProfit > 0 ? 'text-success' : 'text-danger';
    },
    currentUser() {
      return this.$store?.getters?.['auth/currentUser']
        || this.$store?.getters?.['auth/user']
        || this.$store?.state?.auth?.user
        || null;
    },
    canUpdateProduct() {
      return hasPermission(this.currentUser, 'products.update');
    },
    canDeleteProduct() {
      return hasPermission(this.currentUser, 'products.delete');
    },
    canAdjustProductStock() {
      return hasPermission(this.currentUser, 'products.adjust_stock');
    },
  },
  methods: {
    productImage(product) {
      return productImageUrl(product);
    },

    coefficientLabel(value) {
      if (value === null || value === undefined || value === '') return '—';

      const coefficient = Number(value);
      return Number.isFinite(coefficient) ? `x${coefficient.toFixed(2)}` : '—';
    },

    coefficientClass(value) {
      const coefficient = Number(value);
      return Number.isFinite(coefficient) && coefficient <= 1 ? 'text-danger' : '';
    },

    coefficientBadgeClass(value) {
      const coefficient = Number(value);
      return Number.isFinite(coefficient) && coefficient <= 1 ? 'bg-danger' : 'bg-success';
    },

    rayonColor(color) {
      return normalizeRayonColor(color);
    },

    isSystemSupplierOffer(offer) {
      return offer?.supplier?.type === 'system' || offer?.supplier?.system_key === 'default';
    },

    async fetchProduct() {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      const id = this.$route.params.id;

      try {
        const response = await this.$api.get(`/products/${id}`);
        this.product = response.data.data;
        this.fetchProductHistories();
      } catch (error) {
        this.errorMessage = 'Impossible de charger le produit.';
        this.product = null;
      } finally {
        this.isLoading = false;
      }
    },

    fetchProductHistories() {
      this.fetchSalesHistory();
      this.fetchPurchasesHistory();
      this.fetchProductMovements();
    },

    async fetchSalesHistory() {
      if (!this.product?.id) return;

      this.isLoadingSalesHistory = true;

      try {
        const response = await this.$api.get(`/products/${this.product.id}/sales-history`, {
          params: { limit: 50 },
        });
        this.salesHistory = response.data.data || [];
      } catch (error) {
        this.salesHistory = [];
      } finally {
        this.isLoadingSalesHistory = false;
      }
    },

    async fetchPurchasesHistory() {
      if (!this.product?.id) return;

      this.isLoadingPurchasesHistory = true;

      try {
        const response = await this.$api.get(`/products/${this.product.id}/purchases-history`, {
          params: { limit: 50 },
        });
        this.purchasesHistory = response.data.data || [];
      } catch (error) {
        this.purchasesHistory = [];
      } finally {
        this.isLoadingPurchasesHistory = false;
      }
    },

    async fetchProductMovements() {
      if (!this.product?.id) return;

      this.isLoadingMovements = true;

      try {
        const params = {
          product_id: this.product.id,
          paginate: 1,
          per_page: this.movementTable.perPage,
          page: this.movementTable.page,
          sort_by: this.movementTable.sortBy,
          sort_dir: this.movementTable.sortDir,
        };

        Object.entries(this.movementFilters).forEach(([key, value]) => {
          if (value !== null && value !== undefined && String(value).trim() !== '') {
            params[key] = value;
          }
        });

        const response = await this.$api.get('/stock-movements', { params });
        this.movementsResponse = response.data;
      } catch (error) {
        console.error('Erreur mouvements du produit', error);
        this.movementsResponse = null;
      } finally {
        this.isLoadingMovements = false;
      }
    },

    async fetchMovementReasons() {
      this.isLoadingMovementReasons = true;
      try {
        const response = await this.$api.get('/stock-movement-reasons', { params: { is_active: 1 } });
        this.movementReasons = response.data.data || [];
      } catch (error) {
        this.movementReasons = [];
        if (this.stockAdjustmentDialogVisible) {
          this.stockAdjustmentError = 'Impossible de charger les motifs de mouvement.';
        }
      } finally {
        this.isLoadingMovementReasons = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'Products' });
    },

    openEditDialog() {
      if (!this.canUpdateProduct) return;

      this.dialogVisible = true;
    },

    openProductLabelDialog() {
      if (!this.product?.barcode) return;

      this.labelDialogVisible = true;
    },

    closeDialog() {
      this.dialogVisible = false;
    },

    defaultSupplierAttachForm() {
      return {
        supplier_id: null,
        purchase_price: Number(this.product?.purchase_price || 0),
        lead_time_days: null,
        min_order_quantity: null,
        supplier_sku: '',
        is_default: !this.hasDefaultSupplier,
        is_active: true,
        notes: '',
      };
    },

    async fetchSuppliers() {
      this.isLoadingSuppliers = true;

      try {
        const response = await this.$api.get('/suppliers', {
          params: { only_active: 'true', limit: 1000 },
        });
        this.suppliers = response.data.data || response.data || [];
      } catch (error) {
        console.error('Erreur chargement fournisseurs', error);
        this.suppliers = [];
      } finally {
        this.isLoadingSuppliers = false;
      }
    },

    async openAttachSupplierDialog() {
      if (!this.canUpdateProduct) return;

      this.supplierAttachForm = this.defaultSupplierAttachForm();
      this.supplierAttachError = '';
      this.supplierAttachDialogVisible = true;
      await this.fetchSuppliers();
    },

    closeAttachSupplierDialog() {
      if (this.isSavingSupplierAttach) return;

      this.supplierAttachDialogVisible = false;
    },

    async submitSupplierAttach() {
      if (!this.canUpdateProduct) return;
      if (!this.product?.id) return;

      if (!this.supplierAttachForm.supplier_id) {
        this.supplierAttachError = 'Sélectionnez un fournisseur.';
        return;
      }

      this.isSavingSupplierAttach = true;
      this.supplierAttachError = '';
      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.post(`/products/${this.product.id}/suppliers`, {
          supplier_id: this.supplierAttachForm.supplier_id,
          purchase_price: Number(this.supplierAttachForm.purchase_price || 0),
          lead_time_days: this.supplierAttachForm.lead_time_days === null || this.supplierAttachForm.lead_time_days === ''
            ? null
            : Number(this.supplierAttachForm.lead_time_days),
          min_order_quantity: this.supplierAttachForm.min_order_quantity === null || this.supplierAttachForm.min_order_quantity === ''
            ? null
            : Number(this.supplierAttachForm.min_order_quantity),
          supplier_sku: this.supplierAttachForm.supplier_sku || null,
          is_default: !!this.supplierAttachForm.is_default,
          is_active: this.supplierAttachForm.is_active !== false,
          notes: this.supplierAttachForm.notes || null,
        });

        this.supplierAttachDialogVisible = false;
        this.successMessage = 'Fournisseur attaché au produit avec succès.';
        await this.fetchProduct();
      } catch (error) {
        if (error.response?.data?.errors) {
          this.supplierAttachError = Object.values(error.response.data.errors).flat().join(' ');
        } else {
          this.supplierAttachError = 'Impossible d’attacher ce fournisseur au produit.';
        }
      } finally {
        this.isSavingSupplierAttach = false;
      }
    },

    openStockAdjustmentDialog() {
      if (!this.canAdjustProductStock) return;

      this.stockAdjustmentForm = {
        stock_movement_reason_id: '',
        quantity: 1,
        notes: '',
      };
      this.stockAdjustmentError = '';
      this.stockAdjustmentDialogVisible = true;
      if (!this.movementReasons.length) {
        this.fetchMovementReasons();
      }
    },

    closeStockAdjustmentDialog() {
      this.stockAdjustmentDialogVisible = false;
    },

    async submitStockAdjustment() {
      if (!this.canAdjustProductStock) return;
      if (!this.product) return;
      if (this.isSavingStockAdjustment) return;

      if (!this.stockAdjustmentForm.stock_movement_reason_id) {
        this.stockAdjustmentError = 'Sélectionnez un motif pour ajuster le stock.';
        return;
      }

      this.isSavingStockAdjustment = true;
      this.stockAdjustmentError = '';
      this.successMessage = '';
      this.errorMessage = '';

      try {
        const response = await this.$api.post('/stock-movements', {
          product_id: this.product.id,
          stock_movement_reason_id: this.stockAdjustmentForm.stock_movement_reason_id,
          quantity: Number(this.stockAdjustmentForm.quantity || 0),
          notes: this.stockAdjustmentForm.notes || null,
        });

        this.successMessage = response.data.message || 'Stock ajusté avec succès.';
        if (this.selectedAdjustmentReason?.is_supplier_return) {
          this.successMessage = 'Stock ajusté et retour fournisseur créé.';
        }
        this.stockAdjustmentDialogVisible = false;
        await this.fetchProduct();
        await this.fetchProductMovements();
      } catch (error) {
        this.stockAdjustmentError = error?.response?.data?.message || "Impossible d'ajuster le stock.";
      } finally {
        this.isSavingStockAdjustment = false;
      }
    },

    applyMovementFilters() {
      this.movementTable.page = 1;
      this.fetchProductMovements();
    },

    resetMovementFilters() {
      this.movementFilters = defaultMovementFilters();
      this.movementTable.page = 1;
      this.fetchProductMovements();
    },

    handleMovementSortChange({ sortBy, sortDir }) {
      this.movementTable.sortBy = sortBy;
      this.movementTable.sortDir = sortDir;
      this.movementTable.page = 1;
      this.fetchProductMovements();
    },

    handleMovementPageChange(page) {
      this.movementTable.page = page;
      this.fetchProductMovements();
    },

    handleMovementPerPageChange(perPage) {
      this.movementTable.perPage = perPage;
      this.movementTable.page = 1;
      this.fetchProductMovements();
    },

    async handleSaved() {
      this.dialogVisible = false;
      this.successMessage = 'Produit mis à jour avec succès.';
      await this.fetchProduct();
    },

    async confirmDelete() {
      if (!this.canDeleteProduct) return;
      if (!this.product) return;
      if (this.isDeleting) return;

      this.isDeleting = true;

      const ok = await this.$confirmAction(
        `Voulez-vous vraiment supprimer le produit "${this.product.name}" ?`
      );
      if (!ok) {
        this.isDeleting = false;
        return;
      }

      try {
        await this.$api.delete(`/products/${this.product.id}`);
        this.$router.push({ name: 'Products' });
      } catch (error) {
        this.errorMessage =
          'Une erreur est survenue lors de la suppression du produit.';
      } finally {
        this.isDeleting = false;
      }
    },

    async onPhotosChanged() {
      // Recharge le produit pour refléter la nouvelle photo principale
      await this.fetchProduct();
    },

    impactLabel(impact) {
      return {
        entry: 'Entrée',
        exit: 'Sortie',
      }[impact] || impact || '-';
    },

    movementImpact(row) {
      return Number(row?.signed_quantity || 0) >= 0 ? 'entry' : 'exit';
    },

    movementImpactLabel(row) {
      return this.impactLabel(this.movementImpact(row));
    },

    movementImpactClass(row) {
      return this.movementImpact(row) === 'entry' ? 'bg-success' : 'bg-danger';
    },

    reasonTypeLabel(type) {
      return {
        system: 'Système',
        standard: 'Standard',
      }[type] || type || '—';
    },

    sourceLabel(source) {
      return {
        manual: 'Manuel',
        purchase: 'Achat',
        sale: 'Vente',
        sale_cancellation: 'Annulation vente',
        supplier_return: 'Retour fournisseur',
        inventory: 'Inventaire',
        stock_transfer_out: 'Transfert envoyé',
        stock_transfer_in: 'Transfert reçu',
        stock_transfer_cancellation: 'Annulation transfert',
      }[source] || source || '—';
    },

    paymentMethodLabel(method) {
      return {
        cash: 'Espèces',
        card: 'Carte',
        mobile_money: 'Mobile money',
        other: 'Autre',
      }[method] || method || '—';
    },

    isPromotionSaleLine(line) {
      return Number(line.base_unit_price || 0) > Number(line.unit_price || 0)
        || ['planned_promotion', 'cashier_promotion'].includes(String(line.price_source || ''));
    },

  },
};
</script>

<style scoped>
.product-page-heading {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.product-summary-card-body {
  padding: 1rem;
}

.product-summary-layout {
  align-items: stretch;
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1.05fr) minmax(430px, 0.95fr);
}

.product-identity {
  align-items: stretch;
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(132px, 168px) minmax(0, 1fr);
  min-width: 0;
}

.product-summary-photo {
  aspect-ratio: 4 / 5;
  background: #f8fafc;
  border: 1px solid #e5ebf4;
  border-radius: 8px;
  height: 100%;
  min-height: 224px;
  object-fit: cover;
  width: 100%;
}

.product-identity-body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 100%;
  min-width: 0;
}

.product-title-row {
  align-items: flex-start;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  min-width: 0;
}

.product-title {
  font-size: 1.35rem;
  font-weight: 900;
  line-height: 1.18;
  min-width: 0;
  overflow-wrap: anywhere;
}

.product-status-group {
  align-items: center;
  display: flex;
  flex: none;
  flex-wrap: wrap;
  gap: 0.35rem;
  justify-content: flex-end;
}

.product-meta-grid {
  display: grid;
  gap: 0.65rem;
  grid-auto-rows: minmax(58px, auto);
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.product-meta-item {
  background: #f8fafc;
  border: 1px solid #e9eef7;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  justify-content: center;
  min-height: 58px;
  min-width: 0;
  padding: 0.55rem 0.7rem;
}

.product-meta-item span {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 800;
}

.product-meta-item strong {
  color: #0f172a;
  font-size: 0.88rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-summary-side {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: minmax(148px, 0.68fr) minmax(0, 1.32fr);
  min-width: 0;
}

.product-stock-highlight {
  background: #f8fafc;
  border: 1px solid #d8e1ee;
  border-radius: 8px;
  color: #0f172a;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100%;
  min-width: 0;
  padding: 0.85rem;
}

.product-stock-highlight-label {
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 900;
}

.product-stock-highlight strong {
  color: #16a34a;
  font-size: 2.35rem;
  font-weight: 900;
  line-height: 1;
}

.product-stock-highlight.is-low {
  background: #fff7ed;
  border-color: #fed7aa;
}

.product-stock-highlight.is-low strong {
  color: #dc2626;
}

.product-stock-highlight-meta {
  display: grid;
  gap: 0.45rem;
  margin-top: 0.9rem;
}

.product-stock-highlight-meta span {
  background: #ffffff;
  border: 1px solid #e9eef7;
  border-radius: 6px;
  color: #475569;
  font-size: 0.78rem;
  font-weight: 800;
  padding: 0.38rem 0.5rem;
}

.product-kpi-grid {
  display: grid;
  gap: 0.65rem;
  grid-auto-rows: minmax(76px, auto);
  grid-template-columns: repeat(2, minmax(0, 1fr));
  min-width: 0;
}

.product-kpi {
  background: #ffffff;
  border: 1px solid #e5ebf4;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  justify-content: center;
  min-height: 76px;
  min-width: 0;
  padding: 0.65rem 0.7rem;
}

.product-kpi > span {
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 800;
}

.product-kpi .badge {
  color: #ffffff;
}

.product-kpi strong,
.product-kpi-value {
  color: #0f172a;
  font-size: 1rem;
}

.product-kpi-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-kpi small {
  color: #64748b;
  font-weight: 700;
  min-width: 0;
}

.product-kpi-wide {
  grid-column: span 2;
}

.product-description {
  background: #f8fafc;
  border: 1px solid #e9eef7;
  border-radius: 8px;
  padding: 0.75rem 0.9rem;
}

.product-description-inline {
  margin-top: auto;
}

.product-description span {
  color: #64748b;
  display: block;
  font-size: 0.76rem;
  font-weight: 900;
  margin-bottom: 0.25rem;
}

.product-description p {
  color: #334155;
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.product-detail-tabs-card :deep(.p-tabview-nav) {
  border-bottom-color: #e5ebf4;
}

.product-detail-tabs-card :deep(.p-tabview-panels) {
  padding: 1rem 0 0;
}

.empty-tab-state {
  align-items: center;
  background: #f8fafc;
  border: 1px dashed #d8e1ee;
  border-radius: 8px;
  color: #64748b;
  display: flex;
  font-weight: 700;
  justify-content: center;
  min-height: 120px;
  padding: 1.5rem;
  text-align: center;
}

.rayon-badge {
  align-items: center;
  background: #ffffff;
  border: 1px solid;
  border-radius: 999px;
  display: inline-flex;
  gap: 0.4rem;
  max-width: 100%;
  min-width: 0;
  padding: 0.12rem 0.55rem;
}

.rayon-badge span:last-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rayon-color-dot {
  border: 1px solid rgba(15, 23, 42, 0.16);
  border-radius: 999px;
  flex: none;
  display: inline-block;
  height: 0.75rem;
  width: 0.75rem;
}

.supplier-cell {
  display: grid;
  gap: 0.35rem;
  min-width: 180px;
}

.supplier-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

@media (max-width: 1199.98px) {
  .product-summary-layout {
    grid-template-columns: 1fr;
  }

  .product-summary-side {
    grid-template-columns: minmax(180px, 0.42fr) minmax(0, 1fr);
  }
}

@media (max-width: 767.98px) {
  .product-page-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .product-page-heading > .d-flex {
    justify-content: flex-start !important;
    width: 100%;
  }

  .product-identity {
    grid-template-columns: 110px minmax(0, 1fr);
  }

  .product-summary-photo {
    aspect-ratio: 1 / 1;
    min-height: 110px;
  }

  .product-title {
    font-size: 1.08rem;
  }

  .product-title-row {
    flex-direction: column;
    gap: 0.5rem;
  }

  .product-status-group {
    justify-content: flex-start;
  }

  .product-meta-grid {
    grid-template-columns: 1fr;
  }

  .product-summary-side {
    grid-template-columns: 1fr;
  }

  .product-kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .product-kpi-wide {
    grid-column: span 2;
  }
}

@media (max-width: 575.98px) {
  .product-identity {
    grid-template-columns: 1fr;
  }

  .product-summary-photo {
    max-height: 190px;
    min-height: 150px;
  }

  .product-stock-highlight {
    min-height: 132px;
  }

  .product-kpi-grid {
    grid-template-columns: 1fr;
  }

  .product-kpi-wide {
    grid-column: auto;
  }
}
</style>
